import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { FocusShell } from '../components/FocusShell';
import { humanizeError } from '../lib/errors';
import usePageTitle from '../hooks/usePageTitle';

const Login: React.FC = () => {
  const [error, setError] = useState('');
  const auth = useAuth();
  const location = useLocation();
  const from = (location.state as { from?: string })?.from;
  usePageTitle('Sign in');

  useEffect(() => {
    if (auth.error) {
      console.error('Authentication error:', auth.error);
      setError(humanizeError(auth.error, "Couldn't sign you in"));
    }
  }, [auth.error]);

  if (auth.isAuthenticated) {
    return <Navigate to={from || '/my-apps'} replace />;
  }

  const handleOidcLogin = async () => {
    setError('');
    try {
      await auth.signinRedirect({ state: { returnTo: from || '/my-apps' } });
    } catch (err: unknown) {
      console.error('Failed to start sign-in:', err);
      setError(humanizeError(err, "Couldn't start sign-in"));
    }
  };

  const redirecting = auth.activeNavigator === 'signinRedirect';

  return (
    <FocusShell
      title="Welcome to Dory"
      description="Sees everything on the street. Remembers nothing."
      footer={
        <footer className="space-y-1 py-4 text-center text-xs text-muted-foreground">
          <p>&copy; {new Date().getFullYear()} Dory &mdash; I-SENSE, Florida Atlantic University. All rights reserved.</p>
          <div className="flex justify-center gap-4">
            <Link to="/legal/terms-of-service" className="transition-colors hover:text-foreground">Terms of Service</Link>
            <Link to="/legal/privacy-policy" className="transition-colors hover:text-foreground">Privacy Policy</Link>
          </div>
        </footer>
      }
    >
      <Card className="shadow-md">
        <CardHeader className="pb-3">
          <CardTitle className="text-center font-medium">Sign in to your account</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertCircle aria-hidden="true" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <Button
            type="button"
            onClick={handleOidcLogin}
            disabled={auth.isLoading}
            loading={redirecting}
            className="w-full"
          >
            Continue with SSO
          </Button>
        </CardContent>
      </Card>
    </FocusShell>
  );
};

export default Login;
